import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import Decimal from 'decimal.js';
import { Observable } from 'rxjs';
import { distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { OrdersService } from 'src/app/inventory/components/receive-edi/orders.service';
import { ListItem } from 'src/app/shared/components/multiselect-dropdown/multiselect.model';
import { SearchInputColorEnum } from 'src/app/shared/components/search-input/search-input.component';
import { BaseTableDirective } from 'src/app/shared/directives/base-table.directive';
import { IDrugsFilterDrugItem } from 'src/app/shared/interfaces/drugs-filter-drug-item.interface';
import { NavigatedFromPipe } from 'src/app/shared/pipes/navigated-from/navigated-from.pipe';
import { GetDrugsForDrugsFilter } from 'src/app/shared/states/drugs/drugs.actions';
import { DrugsState } from 'src/app/shared/states/drugs/drugs.state';
import { RunSimilarOnHandHistoryReport, SaveSimilarOnHandHistoryReportControlsState, SetLoading, SetSimilarOnHandHistoryRecords } from 'src/app/shared/states/reports/reports.actions';
import { ReportsState } from 'src/app/shared/states/reports/reports.state';
import { areFiltersEmpty, groupByGPI } from '../../helpers';
import { ReportsService } from '../../reports.service';
import { ISohHistoryReportItem } from './interfaces/soh-history-report-item.interface';
import { SideWidgetService } from 'src/app/shared/services/side-widget.service';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { FileExportType } from '../../interfaces/file-export-type.enum';
import { TransactionBadgePipe } from 'src/app/shared/pipes/transaction-badge/transaction-badge.pipe';

@UntilDestroy()
@Component({
  selector: 'app-soh-history-report',
  templateUrl: './soh-history-report.component.html',
  styleUrls: ['../.././reports.component.scss', './soh-history-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SohHistoryReportComponent extends BaseTableDirective implements OnInit {
  @Select(ReportsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(DrugsState.drugsFilterDrugItems) drugsFilterDrugItems$: Observable<IDrugsFilterDrugItem[]>;
  @Select(ReportsState.runningReports) runningReports$: Observable<boolean>;
  @Select(ReportsState.similarOnHandHistoryReportData) similarOnHandHistoryReportData$: Observable<ISohHistoryReportItem[]>;
  @Select(ReportsState.similarOnHandHistoryReportControlsState) similarOnHandHistoryReportControlsState$: Observable<any>;

  form: UntypedFormGroup;

  isDrugsDropdownOpened: boolean;

  drugsList: ListItem[];

  originalRecordsList: ISohHistoryReportItem[];
  sortedRecordsList: any[] = [];

  isReportRunApplied: boolean;
  groupItemsOpened: boolean[] = [];

  SearchInputColorEnum = SearchInputColorEnum;

  isFileDownloading: boolean;

  tableColumns = {
    transactionDate: true,
  };
  
  @ViewChild('downloadMenu') downloadMenu;

  private filterResultsBy = 'gpi';
  private title = 'Similar On-Hand History';

  navigationState: any;
  /**
   *
   */
  constructor(  
    protected store: Store,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private ordersService: OrdersService,
    private reportsService: ReportsService,
    private navigatedFromPipe: NavigatedFromPipe,
    private sideWidgetService: SideWidgetService,
    private transactionBadgePipe: TransactionBadgePipe
    ) {
      super(store);
      this.form = this.fb.group({
        period: ['', []],
        customPeriod: [null, []],
        drugs: [[], []],
        scrollTop: 0,
        inventoryLocations: null,
        searchValue: null
      });
      this.navigationState = this.router.getCurrentNavigation()?.extras?.state;
  }

  ngOnInit(): void {
    if (!this.store.selectSnapshot(DrugsState.drugsFilterDrugItems)?.length) {
      this.store.dispatch(new GetDrugsForDrugsFilter());
    }

    this.drugsFilterDrugItems$
    .pipe(untilDestroyed(this))
    .subscribe(drugs => {
      this.drugsList = drugs?.filter(x => !!x.gpi).map(el =>  ({id: el.id, name: el.friendlyName, itemName: el.friendlyName, code: el.packageNdc, text: el.friendlyName }));
    });

    this.similarOnHandHistoryReportData$
    .pipe(untilDestroyed(this))
    .subscribe(records => {
      if (records) { 
        this.originalRecordsList = records.map(x => ({...x, sohStartingQty: (new Decimal(x.sohEndingQty).add(new Decimal(x.addedQuantity).mul(-1))).toNumber()}));
        this.filterBySearchValue(this.form.get('searchValue').value);

      }
    })

     this.form.get('searchValue')?.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(''),
        distinctUntilChanged(),
      )
      .subscribe((search: string) => {
        this.filterBySearchValue(search);
      });

    //restore filters
    this.similarOnHandHistoryReportControlsState$.pipe(take(1))
      .subscribe(data => {
        this.form.patchValue(data);
        this.cdr.markForCheck();
    });

    if (this.navigationState?.selectedNdc && this.navigationState?.gpi) {
      this.reset();
      this.runReport([this.navigationState.gpi]);
    }
  }

  runReport(gpis?: string[]): void {
       const raw = this.form.getRawValue();
       this.store.dispatch(new SaveSimilarOnHandHistoryReportControlsState(raw));
       this.store.dispatch(new RunSimilarOnHandHistoryReport({
        startDate: raw?.customPeriod?.[0] ? new Date(raw.customPeriod[0]).toDateString() : null,
        endDate: raw?.customPeriod?.[1] ? new Date(raw.customPeriod[1]).toDateString() : null,
        gpis: this.getGPIsFromSelectedDrugs(raw?.drugs?.map(x => x.id) ) || gpis || [],
        searchDateRange: !raw?.period ? 0 : raw.period,
        inventoryLocations: raw?.inventoryLocations?.map(x => x.id)
       }));
   
       this.isReportRunApplied = true;
  }

  private getGPIsFromSelectedDrugs(selectedDrugIDs: string[]): string[] {
    if (!selectedDrugIDs?.length) {
      return;
    }
    const drugs = this.store.selectSnapshot(DrugsState.drugsFilterDrugItems);
    return Array.from(new Set(drugs.filter(x => selectedDrugIDs.includes(x.id))?.map(x => x.gpi)));
  }

   updateSortedList(source: ISohHistoryReportItem[]): void {
      setTimeout(() => {
        this.sortedRecordsList = groupByGPI(source, null, 'transactions');
        this.groupItemsOpened = Array(this.sortedRecordsList.length).fill(true);
        this.store.dispatch(new SetLoading(false));
        this.cdr.markForCheck();
      });
  }

  filterBySearchValue(searchString: string = ''): void {
    searchString = searchString?.replace(/-/g, '');
    const lowerCaseSearchStr = searchString?.toLocaleLowerCase();
    const filterFunction = (search, data) => searchString ?
      data.filter(el => 
        el.gpi?.toString().toLowerCase().includes(lowerCaseSearchStr) ||
        el.friendlyName.toLowerCase().toString().includes(lowerCaseSearchStr) ||
        el.packageNdc.replace(/-/g, '').includes(search)) : data;
    const filtered = filterFunction(searchString, [...this.originalRecordsList || []]);
    this.updateSortedList(filtered);
  }

  openAuditOrOrder(id: string): void {
    const currentTransaction = this.originalRecordsList.find(el => el.transactionID === id);
    if (currentTransaction?.auditId) {
      event.stopImmediatePropagation();
      this.router.navigate(['audits/details', currentTransaction.auditId,], {state : {
        navigatedFrom: this.router.url
      }
      });
    } else if (currentTransaction?.orderId) {
      event.stopImmediatePropagation();
      this.ordersService.navigateToOrderDetails(this.router, currentTransaction?.orderType, currentTransaction.orderId);
    }
  }

  sortByColumn(columnName: string): void {
    this.store.dispatch(new SetSimilarOnHandHistoryRecords(this.sort(this.originalRecordsList, columnName)));
  }
  
  areFiltersEmpty(): boolean {
   return areFiltersEmpty(this.form.value);
  }

  reset(): void {
    this.form.reset();
    this.store.dispatch(new SaveSimilarOnHandHistoryReportControlsState(null));
    this.originalRecordsList = [];
  }

  savePDF(): void {
      this.store.dispatch(new SetLoading(true));
      this.isFileDownloading = true;
      const raw = this.form.getRawValue();
      this.reportsService.downloadSohHistoryPDFReport({
        fileDtos: this.getEntitiesToExport(),
        startDate: raw?.customPeriod?.[0] ? new Date(raw.customPeriod[0]).toDateString() : null,
        endDate: raw?.customPeriod?.[1] ? new Date(raw.customPeriod[1]).toDateString() : null,
        searchDateRange: !raw?.period ? 0 : raw.period,
        byAllDrugs: !raw.drugs?.length,
      })
      .pipe(take(1))
      .subscribe(res => {
        const name = this.title + ' from ' + new Date().toDateString() + '.pdf';
        this.reportsService.handleDownload(res, name);
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }

  private getEntitiesToExport(exportType: FileExportType = FileExportType.Pdf): any[] {
    const groups =  this.sortedRecordsList.map(group => group.transactions.map(x => {
        const localTime = new Date(x.transactionDate).toString();
        const amendColEmpty = exportType === FileExportType.Csv && x?.activityType === ActivityTypesEnum.Amendment && !x.infoSecondary2;
        return {
        date: localTime.split("GMT")[0].trim(),
        User: x.lastName + ", " + x.firstName,
        drug: x['friendlyName'],
        ndc: x['packageNdc'],
        gpi: x.gpi,
        activity: x.activityType,
        activityText: this.transactionBadgePipe.transform(x)?.label,
        location: x?.locationName,
        starting: x.sohStartingQty,
        count: x.addedQuantity,
        onHand: x.sohEndingQty,
        info1: amendColEmpty ? '' : x.infoHeading,
        info2: x.infoSecondary,
        info3: amendColEmpty ? x.infoHeading : x?.infoSecondary2
        }})
      );

    const merged = [];
    groups.forEach(array => {
      merged.push(...array);
    });

    return merged;
  }

  saveCSV(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    this.reportsService.downloadSohHistoryCsvReport({fileDtos: this.getEntitiesToExport(FileExportType.Csv)})
      .pipe(take(1))
      .subscribe(res => {
        const name = this.title + ' from ' + new Date().toDateString() + '.csv';
        this.reportsService.handleDownload(res, name);
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }

  openSidePanel(transactionId: string): void {
    this.sideWidgetService.openSideWidget(transactionId);
  }

  navigateBack() {
    const navigatedFrom = this.navigatedFromPipe.transform(this.navigationState, 'Inventory', 'inventory/ndc').link;
    this.router.navigate([navigatedFrom], {state: {
      isNavigatedBack: true
    }});
  }
}
